import { notNullish } from "./util";
export const isNode = typeof process !== "undefined" && process["release"] && process["release"]["name"] === "node";
export class Environment {
    constructor(overrides) {
        this.application = null;
        this.envObj = {};
        this.reset(overrides);
        if (!isNode && window?.SDK?.application) {
            this.application = { ...window.SDK.application };
        }
    }
    reset(overrides) {
        let environment;
        if (isNode) {
            environment = { ...process.env };
        }
        else if (window?.SDK?.environment) {
            environment = { ...window.SDK.environment };
        }
        else {
            console.warn("Environment was not located, so it's empty");
            environment = {};
        }
        this.envObj = { ...environment, ...(overrides || {}) };
        return this;
    }
    clear() {
        this.envObj = {};
    }
    /**
     * Check that the environment contains the given variable.
     */
    has(key) {
        return this.envObj.hasOwnProperty(key);
    }
    /**
     * Key all keys.
     */
    keys() {
        return Object.keys(this.envObj);
    }
    /**
     * Get the value for an environment variable. Throws an Error when not present.
     */
    get(key, def) {
        if (!this.has(key)) {
            if (def !== undefined) {
                return def;
            }
            throw new Error(`Environment variable ${key} not found. Make sure the proper dependencies have been declared in the service.yml.`);
        }
        return this.envObj[key];
    }
    getInt(key, fallback, base = 10) {
        const value = this.get(key, fallback !== undefined ? String(fallback) : undefined);
        const parsed = parseInt(value, base);
        if (isNaN(parsed)) {
            throw new Error(`Environment variable ${key} is present, but its value "${value}" does not parse as an integer.`);
        }
        return parsed;
    }
    getFloat(key, fallback) {
        const value = this.get(key, fallback !== undefined ? String(fallback) : undefined);
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
            throw new Error(`Environment variable ${key} is present, but its value "${value}" does not parse as a float.`);
        }
        return parsed;
    }
    getBoolean(key, fallback) {
        const value = this.get(key, fallback !== undefined ? String(fallback) : undefined);
        return value === "true" || value === "1";
    }
    getList(key, fallback, separator = ",") {
        const rand = Math.random().toString();
        const value = this.get(key, fallback ? rand : undefined);
        if (value === rand) {
            return fallback;
        }
        else {
            return value
                .split(separator)
                .map(t => t.trim())
                .filter(notNullish);
        }
    }
    /**
     * Get the hostname of an app dependency defined in service.yml.
     */
    hasDep(key) {
        return this.has(this.makeDependencyKey(key));
    }
    /**
     * Get the hostname of an app dependency defined in service.yml.
     */
    dep(key) {
        return this.get(this.makeDependencyKey(key));
    }
    makeDependencyKey(name) {
        return `DEP_${name.replace(/-/g, "_").toUpperCase()}_HOST`;
    }
}
/**
 * The default environment instance. Based on the process env when in node, or a
 * standard global variable in browser.
 */
export let environ = new Environment();
export const isDev = environ.get("NODE_ENV", "unknown") === "development";
export const isProd = environ.get("NODE_ENV", "unknown") === "production";
//# sourceMappingURL=environment.js.map